import { defineStore } from "pinia";
import { useAuthStore } from "@/stores/auth";
import { ClientError } from "@/libs/errors";

export const useCordovaStore = defineStore({
  id: "cordova",
  state: () => ({
    subscriptions: [
      {
        id: "",
        title: "Carregando...",
        state: "",
        description: "Carregando informações da assinatura",
        price: "",
        expiryDate: null,
        canPurchase: false,
      },
    ],
    currentSubscription: undefined,
    subscriptionError: undefined,
    storeReady: false,
    _storeRefreshed: false,
    _iapInited: false,
  }),
  getters: {
    isLoaded: () => typeof cordova !== "undefined",
    // Se o plugin de compras reporta assinatura ativa/pendente, ela veio do Google Play.
    hasGooglePlaySubscription: (state) => !!state.currentSubscription,
  },
  actions: {
    _onDeviceReady() {
      const authStore = useAuthStore();
      if (authStore.userId) {
        this.initCordovaPluginPurchase();
      }
    },

    async initCordovaPluginPurchase() {
      if (this._iapInited) {
        return;
      }
      const authStore = useAuthStore();
      if (!authStore.userId) {
        return;
      }
      if (typeof CdvPurchase === "undefined") {
        console.warn("[IAP] CdvPurchase indisponivel neste ambiente");
        this.storeReady = true;
        return;
      }
      this._iapInited = true;

      // Get v13 API from CdvPurchase namespace
      const { store, Platform, ProductType, Iaptic, LogLevel } = CdvPurchase;

      // Set debug level (optional, remove in production)
      store.verbosity = LogLevel.DEBUG;

      // Register products with platform (required in v13)
      store.register([
        {
          id: "quantofaz.subscription.monthly",
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.GOOGLE_PLAY,
        },
      ]);

      // Configure validator using Iaptic class (v13 way)
      const iaptic = new Iaptic({
        appName: "br.com.quantofaz.app",
        apiKey: "4f4484e2-4c8e-4be2-b100-aa55b794a5ce",
      });
      store.validator = iaptic.validator;

      // Error handler
      store.error((error) => {
        console.error("[IAP] Error:", error);
        this.subscriptionError = error;
      });

      // Transaction flow handlers (v13 style)
      store
        .when()
        .approved((transaction) => {
          console.log("[IAP] Transaction approved:", transaction.transactionId);
          transaction.verify();
        })
        .verified((receipt) => {
          console.log("[IAP] Receipt verified");
          receipt.finish();
        });

      // Product updated handler (v13 style)
      store.when().productUpdated((product) => {
        console.log("[IAP] Product updated:", product.id);
        if (product.id === "quantofaz.subscription.monthly") {
          this._updateSubscriptionState(product, store);
        }
      });

      // Receipt updated handler for ownership changes
      store.when().receiptUpdated((receipt) => {
        console.log("[IAP] Receipt updated");
        const product = store.products.find(
          (p) => p.id === "quantofaz.subscription.monthly"
        );
        if (product) {
          this._updateSubscriptionState(product, store);
        }
      });

      // Set application username for the validator
      store.applicationUsername = () => authStore.userId;

      // Initialize the store (v13 way - async)
      try {
        await store.initialize([Platform.GOOGLE_PLAY]);
        console.log("[IAP] Store initialized");
      } catch (error) {
        console.error("[IAP] Initialization error:", error);
        this.subscriptionError = error;
      } finally {
        this.storeReady = true;
      }
    },

    _updateSubscriptionState(product, store) {
      // Transform v13 product data to the format expected by the view
      const isOwned = store.owned(product.id);

      // Get pricing info (v13 uses product.pricing)
      const pricing = product.pricing;
      const price = pricing ? pricing.price : "";

      // Check for pending transactions
      const hasPendingTransaction = product.transactions?.some(
        (t) => t.state === "approved" || t.state === "initiated"
      );

      // Determine state for UI
      let state = "";
      if (hasPendingTransaction) {
        state = "approved";
      } else if (isOwned) {
        state = "owned";
      }

      // Get expiry date from verified purchase if available
      let expiryDate = null;
      if (product.owned) {
        // Try to get expiry from the latest verified receipt
        const verifiedPurchase = product.transactions?.find(
          (t) => t.isVerified && t.expiryDate
        );
        if (verifiedPurchase) {
          expiryDate = new Date(verifiedPurchase.expiryDate);
        }
      }

      const subscriptionData = {
        id: product.id,
        title: product.title || "Assinatura Mensal",
        description: product.description || "Acesso completo ao QuantoFaz",
        price: price,
        canPurchase: product.canPurchase,
        state: state,
        expiryDate: expiryDate,
      };

      // Update current subscription if owned
      if (isOwned || hasPendingTransaction) {
        this.currentSubscription = subscriptionData;
      } else {
        this.currentSubscription = undefined;
      }

      // Update subscriptions array
      this.subscriptions.splice(0, this.subscriptions.length);
      this.subscriptions.push(subscriptionData);
    },

    refreshStore() {
      if (typeof CdvPurchase === "undefined") {
        return;
      }
      if (!this._storeRefreshed) {
        this._storeRefreshed = true;
        // v13 uses update() instead of refresh()
        const { store } = CdvPurchase;
        store.update();
      }
    },

    async subscribe(subscriptionIndex) {
      this.subscriptionError = undefined;
      if (!this.subscriptions[subscriptionIndex]) {
        this.subscriptionError = new ClientError(
          `Configurações da assinatura não encontrada`
        );
        return;
      }
      const subscription = this.subscriptions[subscriptionIndex];
      if (!subscription.canPurchase) {
        this.subscriptionError = new ClientError(
          `Assinatura não pode ser processada`
        );
        return;
      }

      // v13 purchase flow: find product, get offer, order
      const { store } = CdvPurchase;
      const product = store.products.find((p) => p.id === subscription.id);

      if (!product) {
        this.subscriptionError = new ClientError(
          `Produto não encontrado na loja`
        );
        return;
      }

      const offer = product.getOffer();
      if (!offer) {
        this.subscriptionError = new ClientError(
          `Oferta não disponível para este produto`
        );
        return;
      }

      try {
        await offer.order();
      } catch (error) {
        console.error("[IAP] Order error:", error);
        this.subscriptionError = error;
      }
    },

    openInExternalBrowser(link) {
      cordova.InAppBrowser.open(link, "_system");
    },

    manageSubscriptions() {
      const { store } = CdvPurchase;
      store.manageSubscriptions();
    },
  },
});
